/**
 * Analyseur XML minimal et strict pour les parties d’un classeur OOXML.
 *
 * Volontairement limité : pas de DTD ni d’entités personnalisées (DOCTYPE/ENTITY refusés :
 * pas d’XXE ni d’« milliard de rires »), seules les cinq entités prédéfinies et les références
 * numériques sont décodées, profondeur bornée, espaces de noms ignorés (nom local seul).
 */
import { XLSX_LIMITS, XlsxImportError } from './XlsxLimits';

export type XmlAttrs = Record<string, string>;

export interface XmlHandlers {
  open?: (name: string, attrs: XmlAttrs, selfClosing: boolean) => void;
  close?: (name: string) => void;
  text?: (texte: string) => void;
}

/** Décode un contenu binaire UTF-8 (BOM toléré) ou UTF-16 (BOM obligatoire). */
export function decoderXml(octets: Uint8Array): string {
  try {
    if (octets[0] === 0xff && octets[1] === 0xfe) {
      return new TextDecoder('utf-16le', { fatal: true }).decode(octets.subarray(2));
    }
    if (octets[0] === 0xfe && octets[1] === 0xff) {
      return new TextDecoder('utf-16be', { fatal: true }).decode(octets.subarray(2));
    }
    return new TextDecoder('utf-8', { fatal: true, ignoreBOM: false }).decode(octets);
  } catch {
    throw new XlsxImportError('Encodage de caractères invalide dans le classeur (UTF-8 attendu)');
  }
}

const ENTITE_INCONNUE = /&(?!(?:#x[0-9a-fA-F]{1,6}|#[0-9]{1,7}|amp|lt|gt|quot|apos);)/;

export function decoderEntites(brut: string): string {
  if (brut.indexOf('&') === -1) return brut;
  if (ENTITE_INCONNUE.test(brut)) {
    throw new XlsxImportError('Classeur refusé : entité XML non autorisée');
  }
  return brut.replace(/&(#x[0-9a-fA-F]+|#[0-9]+|amp|lt|gt|quot|apos);/g, (_m, e: string) => {
    switch (e) {
      case 'amp':
        return '&';
      case 'lt':
        return '<';
      case 'gt':
        return '>';
      case 'quot':
        return '"';
      case 'apos':
        return "'";
      default: {
        const cp = e[1] === 'x' ? parseInt(e.slice(2), 16) : parseInt(e.slice(1), 10);
        if (cp === 0 || cp > 0x10ffff || (cp >= 0xd800 && cp <= 0xdfff)) return '';
        return String.fromCodePoint(cp);
      }
    }
  });
}

/** Retire le préfixe d’espace de noms (`x:row` → `row`). */
function nomLocal(nom: string): string {
  const i = nom.indexOf(':');
  return i === -1 ? nom : nom.slice(i + 1);
}

const ESPACE = /\s/;

export function scannerXml(xml: string, h: XmlHandlers): void {
  if (/<!DOCTYPE|<!ENTITY/i.test(xml)) {
    throw new XlsxImportError('Classeur refusé : déclaration DTD/entité XML interdite');
  }
  const encodage = /^\s*<\?xml[^>]*\sencoding\s*=\s*["']([^"']+)["']/i.exec(xml);
  if (encodage && !/^(utf-8|utf8|utf-16)$/i.test(encodage[1]!)) {
    throw new XlsxImportError(`Encodage XML non pris en charge : ${encodage[1]}`);
  }
  const n = xml.length;
  const pile: string[] = [];
  let i = xml.charCodeAt(0) === 0xfeff ? 1 : 0;
  while (i < n) {
    const lt = xml.indexOf('<', i);
    if (lt === -1) {
      if (h.text && i < n) h.text(decoderEntites(xml.slice(i)));
      break;
    }
    if (lt > i && h.text) h.text(decoderEntites(xml.slice(i, lt)));
    if (xml.startsWith('<!--', lt)) {
      const fin = xml.indexOf('-->', lt + 4);
      if (fin === -1) throw new XlsxImportError('XML invalide (commentaire non terminé)');
      i = fin + 3;
      continue;
    }
    if (xml.startsWith('<![CDATA[', lt)) {
      const fin = xml.indexOf(']]>', lt + 9);
      if (fin === -1) throw new XlsxImportError('XML invalide (CDATA non terminé)');
      if (h.text) h.text(xml.slice(lt + 9, fin));
      i = fin + 3;
      continue;
    }
    if (xml.startsWith('<?', lt)) {
      const fin = xml.indexOf('?>', lt + 2);
      if (fin === -1) throw new XlsxImportError('XML invalide (instruction non terminée)');
      i = fin + 2;
      continue;
    }
    if (xml.charCodeAt(lt + 1) === 0x2f /* / */) {
      const fin = xml.indexOf('>', lt + 2);
      if (fin === -1) throw new XlsxImportError('XML invalide (balise fermante non terminée)');
      const nom = nomLocal(xml.slice(lt + 2, fin).trim());
      if (pile.pop() !== nom) throw new XlsxImportError('XML invalide (balises mal imbriquées)');
      h.close?.(nom);
      i = fin + 1;
      continue;
    }
    // Balise ouvrante
    let j = lt + 1;
    while (j < n && !ESPACE.test(xml[j]!) && xml[j] !== '>' && xml[j] !== '/') j++;
    const nom = nomLocal(xml.slice(lt + 1, j));
    if (nom === '') throw new XlsxImportError('XML invalide (nom de balise vide)');
    const attrs: XmlAttrs = {};
    let auto = false;
    for (;;) {
      while (j < n && ESPACE.test(xml[j]!)) j++;
      if (j >= n) throw new XlsxImportError('XML invalide (balise non terminée)');
      const c = xml[j];
      if (c === '>') {
        j++;
        break;
      }
      if (c === '/') {
        if (xml[j + 1] !== '>') throw new XlsxImportError('XML invalide (balise mal formée)');
        auto = true;
        j += 2;
        break;
      }
      const debutNom = j;
      while (j < n && xml[j] !== '=' && !ESPACE.test(xml[j]!) && xml[j] !== '>' && xml[j] !== '/')
        j++;
      const cle = xml.slice(debutNom, j);
      while (j < n && ESPACE.test(xml[j]!)) j++;
      if (xml[j] !== '=') throw new XlsxImportError('XML invalide (attribut sans valeur)');
      j++;
      while (j < n && ESPACE.test(xml[j]!)) j++;
      const guillemet = xml[j];
      if (guillemet !== '"' && guillemet !== "'")
        throw new XlsxImportError('XML invalide (attribut non quoté)');
      const fin = xml.indexOf(guillemet, j + 1);
      if (fin === -1) throw new XlsxImportError('XML invalide (attribut non terminé)');
      if (cle !== 'xmlns' && !cle.startsWith('xmlns:')) {
        attrs[nomLocal(cle)] = decoderEntites(xml.slice(j + 1, fin));
      }
      j = fin + 1;
    }
    if (!auto) {
      pile.push(nom);
      if (pile.length > XLSX_LIMITS.maxXmlDepth) {
        throw new XlsxImportError('Classeur refusé : imbrication XML excessive');
      }
    }
    h.open?.(nom, attrs, auto);
    if (auto) h.close?.(nom);
    i = j;
  }
  if (pile.length > 0) throw new XlsxImportError('XML invalide (balises non fermées)');
}
